import type { User } from '@open-dataroom/shared';
import * as React from 'react';

import { api, ApiError, resetCsrfToken } from '~/lib/api';

function isPublicPath(pathname: string): boolean {
  if (pathname === '/login') return true;
  if (pathname.startsWith('/auth/')) return true;
  if (pathname.startsWith('/invites/')) return true;
  return false;
}

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (token: string) => Promise<void>;
  redeemInvite: (inviteId: string, token: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(
  undefined
);
const AUTH_STATE_EVENT = 'open-dataroom:auth-state';

type AuthSnapshot =
  | { status: 'unknown'; user: null }
  | { status: 'anonymous'; user: null }
  | { status: 'authenticated'; user: User };

let authSnapshot: AuthSnapshot = { status: 'unknown', user: null };

/**
 * Module-level generation counter so in-flight session loads from an unmounted
 * AuthProvider (React Strict Mode) cannot clear auth after invite redeem / login.
 */
let authGeneration = 0;

function bumpAuthGeneration(): number {
  authGeneration += 1;
  return authGeneration;
}

function isAuthGenerationCurrent(generation: number): boolean {
  return generation === authGeneration;
}

function clearAuthenticatedSession(): void {
  publishAuthenticatedUser(null);
}

export function getAuthSnapshot(): AuthSnapshot {
  return authSnapshot;
}

function publishAuthSnapshot(next: AuthSnapshot) {
  authSnapshot = next;
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<AuthSnapshot>(AUTH_STATE_EVENT, { detail: next })
  );
}

export function publishAuthenticatedUser(user: User | null) {
  publishAuthSnapshot(
    user
      ? { status: 'authenticated', user }
      : { status: 'anonymous', user: null }
  );
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<User | null>(() => authSnapshot.user);
  const [isLoading, setIsLoading] = React.useState(
    () => authSnapshot.status === 'unknown'
  );

  const applyAuthenticatedUser = React.useCallback((nextUser: User) => {
    publishAuthenticatedUser(nextUser);
    setUser(nextUser);
    setIsLoading(false);
  }, []);

  const fetchSession = React.useCallback(async () => {
    const generation = bumpAuthGeneration();
    const hadAuthenticatedSession =
      getAuthSnapshot().status === 'authenticated';
    try {
      const { user: sessionUser } = await api.get<{ user: User }>(
        '/api/auth/session'
      );
      if (!isAuthGenerationCurrent(generation)) return;
      applyAuthenticatedUser(sessionUser);
    } catch (err) {
      if (!isAuthGenerationCurrent(generation)) return;
      const deactivated =
        err instanceof ApiError &&
        err.status === 403 &&
        err.message === 'Account deactivated';
      if (deactivated) {
        clearAuthenticatedSession();
        setUser(null);
        setIsLoading(false);
        return;
      }
      // A stale /session 401 must not wipe a session established by invite redeem or verify.
      if (
        hadAuthenticatedSession ||
        getAuthSnapshot().status === 'authenticated'
      ) {
        return;
      }
      setUser(null);
      if (getAuthSnapshot().status === 'unknown') {
        clearAuthenticatedSession();
      }
      setIsLoading(false);
    } finally {
      if (isAuthGenerationCurrent(generation)) {
        setIsLoading(false);
      }
    }
  }, [applyAuthenticatedUser]);

  React.useEffect(() => {
    void fetchSession();
    return () => {
      bumpAuthGeneration();
    };
  }, [fetchSession]);

  React.useEffect(() => {
    const syncFromSnapshot = () => {
      const snapshot = getAuthSnapshot();
      if (snapshot.status === 'authenticated') {
        setUser(snapshot.user);
        setIsLoading(false);
      }
    };

    syncFromSnapshot();

    const onAuthState = (event: Event) => {
      const next = (event as CustomEvent<AuthSnapshot>).detail;
      setUser(next.user);
      setIsLoading(next.status === 'unknown');
    };

    window.addEventListener(AUTH_STATE_EVENT, onAuthState);
    return () => window.removeEventListener(AUTH_STATE_EVENT, onAuthState);
  }, []);

  /** Recover when a stale /session probe cleared React state but the session cookie is still valid. */
  React.useEffect(() => {
    if (user) return;
    if (typeof window === 'undefined') return;
    if (isPublicPath(window.location.pathname)) return;

    let cancelled = false;
    void (async () => {
      try {
        const { user: sessionUser } = await api.get<{ user: User }>(
          '/api/auth/session'
        );
        if (cancelled) return;
        applyAuthenticatedUser(sessionUser);
      } catch {
        /* logged out or probe still racing */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, applyAuthenticatedUser]);

  const login = React.useCallback(
    async (token: string) => {
      bumpAuthGeneration();
      try {
        const { user: verifiedUser } = await api.post<{ user: User }>(
          '/api/auth/verify',
          { token }
        );
        bumpAuthGeneration();
        applyAuthenticatedUser(verifiedUser);
        await fetchSession();
      } catch (e) {
        bumpAuthGeneration();
        clearAuthenticatedSession();
        setUser(null);
        setIsLoading(false);
        throw e;
      }
    },
    [applyAuthenticatedUser, fetchSession]
  );

  const redeemInvite = React.useCallback(
    async (inviteId: string, token: string) => {
      bumpAuthGeneration();
      try {
        const { user: redeemedUser } = await api.post<{
          success: true;
          user: User;
        }>(`/api/invites/${encodeURIComponent(inviteId)}/redeem`, { token });
        bumpAuthGeneration();
        applyAuthenticatedUser(redeemedUser);
        // Redeem already sets the session cookie; avoid an immediate /session probe that
        // can race with the initial mount probe and clear React state (see e2e invite-redeem).
      } catch (e) {
        bumpAuthGeneration();
        clearAuthenticatedSession();
        setUser(null);
        setIsLoading(false);
        throw e;
      }
    },
    [applyAuthenticatedUser]
  );

  const logout = React.useCallback(async () => {
    bumpAuthGeneration();
    resetCsrfToken();
    api.post('/api/auth/logout', {}).catch(() => {
      /* best-effort */
    });
    clearAuthenticatedSession();
    setUser(null);
    window.location.href = '/login';
  }, []);

  const value = React.useMemo(
    () => ({
      user,
      isLoading,
      isAuthenticated: !!user,
      login,
      redeemInvite,
      logout
    }),
    [user, isLoading, login, redeemInvite, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');

  const snapshot = getAuthSnapshot();
  const user =
    ctx.user ?? (snapshot.status === 'authenticated' ? snapshot.user : null);

  return {
    ...ctx,
    user,
    isAuthenticated: !!user
  };
}
